import * as Sentry from "@sentry/react";
import React, { createContext, Suspense, useContext, useEffect, useState } from "react";
import GAnalytic from "react-ga4";
import TagManager from "react-gtm-module";
import { withRouter } from "react-router-dom";

const CookiePolicyDrawer = React.lazy(() => import("@components/CookiePolicyDrawer"));

const isCookieControlFeatureEnabled =
	window.HAS_COOKIE_POLICY_DRAWER || window.HAS_COOKIE_POLICY_DRAWER === "true" || false;

let toggleConsent = () => {};

let getCookie = () => {
	return isCookieControlFeatureEnabled
		? localStorage.getItem("_cookie_consent") === "true"
		: true;
};

let cookieWasSet = () => {
	return localStorage.getItem("_cookie_consent") !== null;
};

const CookieContext = createContext({
	cookieStatus: getCookie(),
	setCookieStatus: () => {},
	setShowConsent: toggleConsent,
});

const CookieProvider = withRouter(({ children, location }) => {
	const [showConsent, setShowConsent] = useState(false);
	const [cookieStatus, setCookieStatus] = useState(() => getCookie());

	useEffect(() => {
		const isDocsPath = /^\/docs(\/|$)/.test(location.pathname);
		const isCookieConsentSet = cookieWasSet();

		if (isDocsPath || !isCookieControlFeatureEnabled || isCookieConsentSet) {
			return;
		}

		const timer = setTimeout(() => {
			setShowConsent(true);
		}, 2000);
		return () => clearTimeout(timer);
	}, [location.pathname]);

	const handleAccept = () => {
		setCookieStatus(true);
		setShowConsent(false);
		localStorage.setItem("_cookie_consent", true);
	};

	const handleDecline = () => {
		setCookieStatus(false);
		setShowConsent(false);
		localStorage.setItem("_cookie_consent", false);
	};

	useEffect(() => {
		toggleConsent = (consent) => {
			if (!isCookieControlFeatureEnabled) return;
			setShowConsent(consent);
		};
	}, []);

	useEffect(() => {
		if (cookieStatus) {
			GAnalytic.initialize(window.gaConfig);
			TagManager.initialize({
				gtmId: window.gtagConfig,
			});

			Sentry.init({
				dsn: window.DSN,
				integrations: [
					new Sentry.BrowserTracing({
						// Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
						tracePropagationTargets: ["localhost", window.DSN_TRACIKING_REGEX],
					}),
					new Sentry.Replay({
						maskAllText: false,
						maskAllInputs: false,
						blockAllMedia: false,
					}),
				],
				whitelistUrls: [window.MAIN_DOMAIN],
				release: window.RELEASE_VERSION,
				// Performance Monitoring
				tracesSampleRate: 1.0, // Capture 100% of the transactions
				// Session Replay
				replaysSessionSampleRate: 0.001, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
				replaysOnErrorSampleRate: 0.25, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
			});
		}
	}, [cookieStatus]);

	return (
		<CookieContext.Provider value={{ cookieStatus, setCookieStatus, showConsent }}>
			{children}
			<Suspense fallback={null}>
				<CookiePolicyDrawer
					open={showConsent}
					onAccept={handleAccept}
					onDecline={handleDecline}
				/>
			</Suspense>
		</CookieContext.Provider>
	);
});

const useCookie = () => {
	const context = useContext(CookieContext);

	if (!context) {
		throw new Error("useCookie must be used within a CookieProvider");
	}

	return context;
};

export { CookieProvider, cookieWasSet, getCookie, toggleConsent, useCookie };
